import TagManager from '../../classes/TagManager';
import { getCartRemovingActionContext } from '../../utils/helpers';
import app from '@frontastic/catwalk/src/js/app/app';
import Message from '@frontastic/catwalk/src/js/app/message';
import React from 'react';
import { throwFetchErrors } from '../../utils/throwFetchErrors';
import GetCSRFToken from '@srcDS/components/atoms/getCSRFToken';

export const updateCart = (cartInformation, errorCallback) => {
  app.getStore().dispatch({
    type: 'CartApi.Cart.loading',
  });

  return app.getApi().request(
    'POST',
    'Frontastic.CartApi.Cart.update',
    { ownErrorHandler: true },
    cartInformation,
    (data) => {
      app.getStore().dispatch({
        type: 'CartApi.Cart.update.success',
        data: data,
      });
    },
    (error) => {
      app.getStore().dispatch({
        type: 'CartApi.Cart.update.error',
        error: error,
      });
      errorCallback(error?.message);
      throwFetchErrors(error);
    },
  );
};

export const register = async (user, redirect = true, refresh = true, errorCallback, source = '') => {
  const store = app.getStore();
  const { csrf_token: CSRFToken } = user;
  let csrf_token = CSRFToken;

  if (!csrf_token) {
    csrf_token = await GetCSRFToken('register');
  }

  const userData = {
    ...user,
    csrf_token,
    email: user?.email?.toLowerCase(),
    account: {
      email_validation_code: user?.email_validation_code,
      email_validation_response: user?.email_validation_response,
    },
  };
  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.register',
    { ownErrorHandler: true, source },
    userData,
    async (json) => {
      await app
        .getLoader('context')
        .notifyUser(
          <Message code="account.message.registered" message="Account confirmed. Thank you for creating an account." />,
          'success',
        );
      await store.dispatch({
        type: 'Frontastic.AccountApi.Api.register.success',
        data: json,
      });

      await TagManager.newsLetterSubscribe(user.email);

      if (refresh) {
        await app.getLoader('context').refresh();
      }

      if (redirect) {
        // replaced with a native method since there are problems with the Frontastic method and updating the global state
        if (typeof window !== 'undefined') {
          window.location.assign('/account/profile');
        }
      }
      return json;
    },
    (error) => {
      app.getStore().dispatch({
        type: 'ApiBundle.Api.context.error',
        data: error.message,
      });
      errorCallback(error.message);
      error?.status && throwFetchErrors(error);
    },
  );
};

const updateCartAddress = async (cartInformation, errorCallback) => {
  return await app.getApi().request(
    'POST',
    'Frontastic.CartApi.Cart.update',
    { ownErrorHandler: true },
    cartInformation,
    (data) => {},
    (error) => {
      errorCallback && errorCallback(error);
      throwFetchErrors(error);
    },
  );
};

export const login = async ({
  email,
  password,
  csrf_token: CSRFToken,
  recaptcha_token,
  previous = null,
  errorCallback = () => {},
  source = '',
  email_validation_code,
  email_validation_response,
  successCallback = () => {},
}) => {
  let csrf_token = CSRFToken;

  if (!csrf_token) {
    csrf_token = await GetCSRFToken('login');
  }

  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.login',
    { ownErrorHandler: true, source },
    {
      email,
      password,
      csrf_token,
      recaptcha_token,
      account: {
        email_validation_code,
        email_validation_response,
      },
    },
    async (json) => {
      app.getLoader('context').refresh();
      if (email_validation_code && email_validation_response) {
        await updateCartAddress({
          account: {
            email,
            email_validation_code,
            email_validation_response,
          },
        });
        successCallback(json);
      }
      if (previous) {
        app.getRouter().replace(previous.route, previous.parameters);
      }
    },
    (error) => {
      app.getStore().dispatch({
        type: 'ApiBundle.Api.context.error',
        data: error.message,
      });
      errorCallback(error.message);
      throwFetchErrors(error);
    },
  );
};

// Discount utils
const setDiscountMessage = (type, message) => {
  app.getStore().dispatch({
    type: 'UI.discountMessageUpdate',
    data: {
      type,
      message,
    },
  });
};

const setDiscountLoading = (value) => {
  app.getStore().dispatch({
    type: 'UI.discountUpdateLoading',
    data: value,
  });
};

export const redeemDiscount = (promoCode, errorCallback) => {
  if (!promoCode) {
    return;
  }
  app.getStore().dispatch({
    type: 'CartApi.Cart.loading',
  });
  setDiscountLoading({
    type: 'add',
    value: true,
  });
  setDiscountMessage({});

  return app.getApi().request(
    'POST',
    'Frontastic.CartApi.Cart.redeemDiscount',
    { ownErrorHandler: true, code: promoCode },
    null,
    (data) => {
      if (data.cart.discountCodes.some(({ code }) => code === promoCode)) {
        setDiscountMessage('success', `Promo code (${promoCode}) applied successfully.`);
        setDiscountLoading({
          type: 'add',
          value: false,
        });
        TagManager.promoCodeAttempt({
          promoCodeResult: 'Success',
          promoCode: promoCode,
          cartItem: data.cart.lineItems,
        });
      }
    },
    (error) => {
      setDiscountMessage('error', `′${promoCode}′ is not a valid code`);
      setDiscountLoading({
        type: 'add',
        value: false,
      });
      TagManager.promoCodeAttempt({
        promoCode: promoCode,
        promoCodeResult: 'Failed',
      });
      errorCallback({
        type: 'error',
        message: error.message,
      });
    },
  );
};

export const removeDiscount = (discountId) => {
  if (!discountId) {
    return;
  }
  app.getStore().dispatch({
    type: 'CartApi.Cart.loading',
  });
  setDiscountLoading({
    type: 'remove',
    value: true,
  });
  setDiscountMessage({});

  return app.getApi().request(
    'POST',
    'Frontastic.CartApi.Cart.removeDiscount',
    { ownErrorHandler: true },
    { discountId: discountId },
    (data) => {
      setDiscountMessage('success', 'Promo code has been successfully removed.');
      setDiscountLoading({
        type: 'remove',
        value: false,
      });
      setTimeout(() => setDiscountMessage({}), 5000);
      return data;
    },
    (error) => {
      app.getLoader('context').notifyUser(<Message {...error} />, 'error');
      app.getStore().dispatch({
        type: 'CartApi.Cart.update.error',
        error: error,
      });
      setDiscountMessage('success', 'An error has occurred. Please try later.');
      setDiscountLoading({
        type: 'remove',
        value: false,
      });
    },
  );
};

// these 2 actions (add and remove) copied from @frontastic/catwalk package

export const addCartLineItem = (product = null, variant, count, option = null) => {
  const store = app.getStore();
  const context = { sku: variant?.sku };

  store.dispatch({
    type: 'CartApi.Cart.loading',
    context,
  });

  return app.getApi().request(
    'POST',
    'Frontastic.CartApi.Cart.add',
    { ownErrorHandler: true },
    { variant, count, option },
    (data) => {
      app
        .getLoader('context')
        .notifyUser(<Message code="account.message.cartAdd" message="Added product to cart" />, 'success');
      store.dispatch({
        type: 'CartApi.Cart.add.success',
        data,
        context,
      });
    },
    (error) => {
      app.getLoader('context').notifyUser(<Message {...error} />, 'error');
      store.dispatch({
        type: 'CartApi.Cart.add.error',
        error,
        context,
      });
    },
  );
};

export const removeCartLineItem = (update) => {
  const store = app.getStore();
  const context = getCartRemovingActionContext(store, update.lineItemId);

  store.dispatch({
    type: 'CartApi.Cart.loading',
  });

  return app.getApi().request(
    'POST',
    'Frontastic.CartApi.Cart.removeLineItem',
    { ownErrorHandler: true },
    update,
    (data) => {
      app
        .getLoader('context')
        .notifyUser(<Message code="account.message.cartRemove" message="Removed product from cart" />, 'success');
      store.dispatch({
        type: 'CartApi.Cart.remove.success',
        data,
        context,
      });
      TagManager.removeFromCart(update, data.cart);
    },
    (error) => {
      app.getLoader('context').notifyUser(<Message {...error} />, 'error');
      store.dispatch({
        type: 'CartApi.Cart.remove.error',
        error,
        context,
      });
    },
  );
};

export const checkout = (cartInformation) => {
  const store = app.getStore();
  store.dispatch({
    type: 'CartApi.Cart.loading',
  });
  store.dispatch({
    type: 'UI.setCheckoutPanelLoading',
    data: true,
  });

  return app
    .getApi()
    .request(
      'POST',
      'Frontastic.CartApi.Cart.checkout',
      { ownErrorHandler: true },
      cartInformation,
      (data) => {
        store.dispatch({
          type: 'CartApi.Cart.checkout.success',
          data: data,
        });
      },
      (error) => {
        store.dispatch({
          type: 'CartApi.Cart.checkout.error',
          error: error,
        });
      },
    )
    .then(
      (data) => {
        app.getRouter().push('Frontastic.Frontend.Master.Checkout.finished', {
          order: data.order.orderId,
          token: (data.order && data.order.projectSpecificData && data.order.projectSpecificData.viewToken) || null,
        });
        return data;
      },
      (error) => {
        app.getLoader('context').notifyUser(<Message {...error} />, 'error');
        return error;
      },
    )
    .finally(() => {
      store.dispatch({
        type: 'UI.setCheckoutPanelLoading',
        data: false,
      });
    });
};

export const updateAddress = (address, showNotification = true) => {
  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.updateAddress',
    { ownErrorHandler: true },
    address,
    (json) => {
      if (showNotification) {
        app
          .getLoader('context')
          .notifyUser(<Message code="account.message.addressUpdated" message="Updated address" />, 'success');
      }

      // const store = app.getStore();
      // let route = store.getState().app.route;
      // app.getLoader('node').loadMaster(route.route, route.parameters);
      app.getLoader('context').refresh();
    },
    (json) => {
      app.getLoader('context').notifyUser(<Message {...json} />, 'error');
    },
  );
};

export const removeAddress = (address, showNotification = true) => {
  // const store = app.getStore();
  // let route = store.getState().app.route;

  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.removeAddress',
    { ownErrorHandler: true },
    address,
    (json) => {
      if (showNotification) {
        app
          .getLoader('context')
          .notifyUser(<Message code="account.message.addressRemoved" message="Removed address" />, 'success');
      }
      // app.getLoader('node').loadMaster(route.route, route.parameters);
      app.getLoader('context').refresh();
      return json;
    },
    (json) => {
      app.getLoader('context').notifyUser(<Message {...json} />, 'error');
      return json;
    },
  );
};

export const resetPassword = (token, newPassword) => {
  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.reset',
    { ownErrorHandler: true, token: token },
    { newPassword: newPassword },
    async (json) => {
      app
        .getLoader('context')
        .notifyUser(<Message code="account.message.passwordUpdate" message="Password updated" />, 'success');
      await app.getLoader('context').refresh();
      await app.getRouter().replace('Frontastic.Frontend.Master.Account.profile');
    },
    (json) => {
      app.getLoader('context').notifyUser(<Message {...json} />, 'error');
    },
  );
};

const resetPasswordSuccessMessage = 'We sent you an email to reset your password';

export const requestPasswordReset = async (email, setNotification) => {
  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.requestReset',
    { ownErrorHandler: true },
    { email: email },
    () => {
      setNotification({
        email,
        status: 'success',
        message: resetPasswordSuccessMessage,
      });
    },
    (json) => {
      setNotification({
        email,
        status: 'error',
        message: resetPasswordSuccessMessage,
      });
    },
  );
};

/**
 * Update password action
 *
 * @param oldPassword
 * @param newPassword
 * @param csrfToken
 *
 * @returns {*}
 */
export const updatePassword = (oldPassword, newPassword, csrfToken) => {
  return app.getApi().request(
    'POST',
    'Frontastic.AccountApi.Api.changePassword',
    { ownErrorHandler: true },
    {
      oldPassword: oldPassword,
      newPassword: newPassword,
      csrf_token: csrfToken,
    },
    (json) => {
      app.getLoader('context').refresh();

      app
        .getLoader('context')
        .notifyUser(<Message code="account.message.passwordUpdate" message="Password updated" />, 'success');
    },
    (json) => {
      app.getLoader('context').notifyUser(<Message {...json} />, 'error');
    },
  );
};
